import React from 'react';
import { Shield, CheckCircle2, TrendingUp, Clock, Building2, ArrowRight, Calendar } from 'lucide-react';

const ShelfCorporationsPage = () => {
  const shelfCorps = [
    {
      id: 1,
      title: "3-Year Aged Shelf Corporation",
      age: "3",
      description: "Acquire an established 3-year aged corporation ready for immediate use. This shelf corporation comes with a clean history, no liabilities, and established corporate records. Perfect for entrepreneurs who need business credibility fast. An aged corporation signals stability to lenders, vendors, and partners - giving you a head start on building business credit and securing contracts that require established business history.",
      features: [
        "3 Years Corporate History",
        "Clean Record - No Liabilities",
        "Corporate Documents Included",
        "EIN Transfer Ready",
        "Immediate Ownership Transfer",
        "Business Credit Ready",
        "Lender & Vendor Credibility",
        "Contract Eligibility"
      ],
      paymentLink: "https://www.fanbasis.com/agency-checkout/THEACADEMYCEO/mOEMA"
    },
    {
      id: 2,
      title: "5-Year Aged Shelf Corporation",
      age: "5",
      description: "Step into an established 5-year aged corporation with proven corporate longevity. This mid-tier shelf corporation provides significant credibility advantages - lenders and vendors see a 5-year track record as a sign of stability and lower risk. Ideal for securing larger contracts, applying for business credit lines, and establishing vendor relationships that require demonstrated business history.",
      features: [
        "5 Years Corporate History",
        "Clean Record - No Liabilities",
        "Corporate Documents Included",
        "EIN Transfer Ready",
        "Immediate Ownership Transfer",
        "Enhanced Lender Credibility",
        "Mid-Size Contract Eligibility",
        "Vendor Account Approvals"
      ],
      paymentLink: "https://www.fanbasis.com/agency-checkout/THEACADEMYCEO/nxzND"
    },
    {
      id: 3,
      title: "8-Year Aged Shelf Corporation",
      age: "8",
      description: "Our premium 8-year aged shelf corporation offers maximum credibility and business positioning. An 8-year corporate history opens doors that newer businesses simply cannot access. This is the gold standard for entrepreneurs pursuing large contracts, significant credit lines, SBA loans, and high-value vendor relationships. Immediate ownership transfer with complete corporate documentation.",
      features: [
        "8 Years Corporate History",
        "Clean Record - No Liabilities",
        "Complete Corporate Documents",
        "EIN Transfer Ready",
        "Immediate Ownership Transfer",
        "Maximum Lender Credibility",
        "Large Contract Eligibility",
        "Premium Vendor Access"
      ],
      paymentLink: "https://www.fanbasis.com/agency-checkout/THEACADEMYCEO/oVO6N"
    }
  ];

  return (
    <div className="dark-page tradelines-page">
      {/* Hero Section */}
      <section className="tradelines-hero">
        <div className="tradelines-hero-content">
          <div className="tradelines-badge">INSTANT CREDIBILITY</div>
          <h1 className="display-huge tradelines-title">
            <span className="text-gradient">Shelf</span> Corporations
          </h1>
          <p className="body-large tradelines-subtitle">
            Acquire an aged corporation with established history. Skip the waiting period and 
            gain instant business credibility with lenders, vendors, and contract opportunities.
          </p>
          
          <div className="tradelines-features-row">
            <div className="tradeline-feature-item">
              <Calendar size={24} />
              <span>3, 5 & 8 Year Options</span>
            </div>
            <div className="tradeline-feature-item">
              <Shield size={24} />
              <span>Clean History</span>
            </div>
            <div className="tradeline-feature-item">
              <Building2 size={24} />
              <span>Immediate Transfer</span>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee Banner */}
      <div className="guarantee-banner">
        <div className="guarantee-content">
          <CheckCircle2 size={24} />
          <p>All shelf corporations come with clean records, no liabilities, and complete documentation</p>
        </div>
      </div>

      {/* Shelf Corps Grid */}
      <section className="tradelines-grid-section">
        <div className="section-header">
          <h2 className="display-medium section-title">Choose Your Corporate Age</h2>
          <p className="body-medium section-description">
            Select the shelf corporation that matches your credibility needs
          </p>
        </div>
        
        <div className="tradelines-grid">
          {shelfCorps.map((corp) => (
            <div key={corp.id} className="tradeline-card shelf-corp-card" data-testid="shelf-corp-card">
              <div className="shelf-corp-age">
                <span className="age-number">{corp.age}</span>
                <span className="age-label">YEARS</span>
              </div>
              
              <div className="tradeline-card-header">
                <h3 className="tradeline-card-title">{corp.title}</h3>
              </div>
              
              <p className="tradeline-card-description">{corp.description}</p>
              
              <ul className="tradeline-card-features">
                {corp.features.slice(0, 6).map((feature, idx) => (
                  <li key={idx} className="tradeline-feature">
                    <CheckCircle2 size={16} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="tradeline-card-footer">
                <a 
                  href={corp.paymentLink} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="btn-primary tradeline-buy-btn"
                  data-testid="shelf-corp-buy-btn"
                >
                  Learn More
                  <ArrowRight size={20} />
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why Shelf Corps Section */}
      <section className="position-explainer">
        <div className="section-header">
          <h2 className="display-medium section-title">Why Buy a Shelf Corporation?</h2>
          <p className="body-medium section-description">
            Instant credibility that takes years to build organically
          </p>
        </div>
        
        <div className="process-steps">
          <div className="process-step">
            <div className="process-step-number">1</div>
            <div className="process-step-content">
              <h3>Instant Business History</h3>
              <p>Skip years of waiting. An aged corporation gives you immediate credibility that would otherwise take 3, 5, or 8 years to establish.</p>
            </div>
          </div>
          <div className="process-step">
            <div className="process-step-number">2</div>
            <div className="process-step-content">
              <h3>Lender & Vendor Trust</h3>
              <p>Banks and vendors prefer established businesses. An aged corporation significantly improves your approval odds for credit lines, loans, and vendor accounts.</p>
            </div>
          </div>
          <div className="process-step">
            <div className="process-step-number">3</div>
            <div className="process-step-content">
              <h3>Contract Opportunities</h3>
              <p>Many contracts require businesses to have been established for a minimum number of years. A shelf corporation lets you qualify immediately.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="tradelines-cta-section">
        <div className="tradelines-cta-content">
          <h2 className="display-medium">Not Sure Which Age You Need?</h2>
          <p className="body-large">
            Book a strategy call and we'll help you determine which shelf corporation 
            best fits your business goals and funding objectives.
          </p>
          <a 
            href="https://calendly.com/thecollinsacademynew/30min" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="btn-primary"
          >
            Schedule Consultation
            <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default ShelfCorporationsPage;
